import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { useDispatch, useSelector } from 'react-redux';
import { setStage } from '../../../../redux/tatWizardSlice';
import { STAGES } from '../constants';

// Pill stepper — same visual language as the Pricing wizard's WizardStepper so the
// two onboarding modules feel like one product. Stage 1's label is contextual:
// "Pricing Submitted" (new) vs "Merchant Selected" (existing).
export default function TatStepper() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const stage = useSelector((s) => s.tatWizard.stage);
  const flowMode = useSelector((s) => s.tatWizard.flowMode);

  const shortFor = (s) => (s.id === 0 && flowMode === 'existing' ? 'Merchant Selected' : s.short);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 56,
        zIndex: 35,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        borderRadius: '0 0 12px 12px',
        boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          px: 2.5,
          py: 0.9,
          maxWidth: 1640,
          mx: 'auto',
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {STAGES.map((s, idx) => {
          const isDone = s.id < stage;
          const isActive = s.id === stage;
          const isLocked = s.id > stage;

          return (
            <React.Fragment key={s.id}>
              {idx > 0 && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 10,
                    height: 1.5,
                    borderRadius: 99,
                    bgcolor: isDone || isActive ? 'primary.main' : 'grey.200',
                    transition: 'background-color .25s',
                  }}
                />
              )}

              <Box
                onClick={() => { if (!isLocked) dispatch(setStage(s.id)); }}
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.6,
                  borderRadius: '20px',
                  cursor: isLocked ? 'default' : 'pointer',
                  userSelect: 'none',
                  transition: 'background-color .18s, box-shadow .18s',
                  ...(isActive && {
                    bgcolor: 'primary.main',
                    boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.35)}`,
                    '&:hover': { bgcolor: 'primary.dark' },
                  }),
                  ...(isDone && {
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.17) },
                  }),
                  ...(isLocked && { opacity: 0.5 }),
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    flexShrink: 0,
                    fontSize: 10,
                    fontWeight: 800,
                    ...(isActive && { bgcolor: 'rgba(255,255,255,0.25)', color: '#fff' }),
                    ...(isDone && { bgcolor: 'success.main', color: '#fff' }),
                    ...(isLocked && { bgcolor: 'grey.200', color: 'grey.500' }),
                  }}
                >
                  {isDone ? <CheckIcon sx={{ fontSize: 12 }} /> : s.id + 1}
                </Box>
                <Typography
                  noWrap
                  sx={{
                    fontSize: 11.5,
                    fontWeight: isActive ? 700 : isDone ? 600 : 500,
                    lineHeight: 1,
                    color: isActive ? '#fff' : isDone ? 'success.dark' : 'text.disabled',
                  }}
                >
                  {shortFor(s)}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}
